import React from "react";
import { motion } from "framer-motion";
import Footer from "@/components/Footer";
import { Quotes, PenNib, Globe, UsersThree } from "@phosphor-icons/react";

export default function AboutUs() {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="eyebrow text-muted-foreground mb-6">Our Story</div>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-[0.95] mb-8">
              A home for<br /><span className="italic font-medium">digital</span><br />thought.
            </h1>
            <p className="max-w-2xl mx-auto text-lg sm:text-xl text-muted-foreground leading-relaxed mt-10">
              Inkspot was founded on a simple premise: the internet deserves better writing, 
              and writers deserve a better home. We're building a platform that prioritizes 
              the reader's focus and the author's autonomy.
            </p>
          </motion.div>
        </section>

        {/* Mission Tetris */}
        <section className="bg-foreground text-background py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-12 gap-12 items-center">
              <div className="col-span-12 lg:col-span-6">
                <h2 className="font-serif text-4xl sm:text-5xl font-black tracking-tight leading-tight">
                  We believe in the power of the long-form.
                </h2>
                <div className="mt-8 space-y-6 text-background/80 text-lg leading-relaxed">
                  <p>
                    In an age of fragmented attention and algorithmically-driven feeds, 
                    we stand for the deep dive. The researched essay. The personal reflection. 
                    The story that takes time to tell.
                  </p>
                  <p>
                    Our mission is to empower independent voices by giving them the tools 
                    to build a professional presence without the technical overhead. 
                    Inkspot is more than a tool; it's a community of thinkers.
                  </p>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-5 lg:col-start-8">
                <div className="grid grid-cols-1 gap-8">
                  {[
                    { icon: PenNib, title: "Writer First", body: "We build tools that get out of the way so you can focus on the next sentence." },
                    { icon: UsersThree, title: "Community Driven", body: "Discovery isn't just about algorithms; it's about connecting curious minds." },
                    { icon: Globe, title: "Open & Accessible", body: "Your site is public, SEO-ready, and owned by you from day one." }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-6 items-start">
                      <div className="bg-background/10 p-3">
                        <item.icon size={28} weight="duotone" className="text-background" />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl font-bold">{item.title}</h3>
                        <p className="text-sm text-background/60 mt-1 leading-relaxed">{item.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quote Section */}
        <section className="max-w-5xl mx-auto px-6 py-24 text-center">
          <Quotes size={48} weight="fill" className="mx-auto text-muted-foreground/20 mb-8" />
          <h2 className="font-serif text-3xl sm:text-4xl italic font-medium leading-relaxed">
            "Inkspot is the bridge between the simplicity of a typewriter and the 
            reach of the modern web. It's where I go when I have something to say."
          </h2>
          <div className="mt-8 eyebrow text-muted-foreground">— Sarah Jenkins, Author</div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
